import { useState } from 'react';
import type { DuplicateGroup, DuplicatePhoto } from '@photos/shared';
import { useDismissDuplicates, useDuplicates, useUpdatePhotos } from '../../api/hooks';
import { Icon } from '../../components/Icon';
import { fmtCount, wallClock } from '../../lib/format';
import { VISIBILITY_LOOK } from '../../lib/visibility';
import { useToast } from '../../state/toasts';
import { StudioLightbox } from './StudioLightbox';
import { useUndoToast } from './studioUi';

const groupKey = (g: DuplicateGroup) => g.photos.map((p) => p.id).join('|');

const REASON: Record<DuplicateGroup['reasons'][number], string> = {
  burst: 'Same camera, taken within 2 seconds',
  similar: 'Look alike',
};

/** "Jul 22, 2026, 10:00:01 AM": seconds matter when telling a burst apart. */
const takenLabel = (iso: string) =>
  wallClock(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });

/** File sizes to compare re-encodes: "640 KB", "4.2 MB". */
const sizeLabel = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

function copyLabel(p: DuplicatePhoto, group: DuplicatePhoto[]): string | null {
  if (!p.copiedFrom) return null;
  const src = group.find((x) => x.id === p.copiedFrom);
  return src ? `Copy of ${src.fileName}${src.albumName ? ` in ${src.albumName}` : ''}` : 'Copy of another photo';
}

function DuplicateCard({ p, group, onOpen, onTrash, busy }: { p: DuplicatePhoto; group: DuplicatePhoto[]; onOpen(): void; onTrash(): void; busy: boolean }) {
  const look = VISIBILITY_LOOK[p.visibility];
  const copy = copyLabel(p, group);
  return (
    <div className="dupcard">
      <button type="button" className="dupthumb" aria-label={`Open ${p.fileName}`} onClick={onOpen}>
        <img src={p.thumbUrl} alt="" loading="lazy" />
      </button>
      <dl className="kv">
        <dt>Camera</dt>
        <dd>{p.camera || '—'}</dd>
        <dt>Taken</dt>
        <dd>{takenLabel(p.takenAt)}</dd>
        <dt>Size</dt>
        <dd>
          {p.width} × {p.height} · {sizeLabel(p.bytes)}
        </dd>
        <dt>File</dt>
        <dd>{p.fileName}</dd>
        <dt>Uploaded by</dt>
        <dd>{p.uploaderName}</dd>
        <dt>Album</dt>
        <dd>{p.albumName ?? '—'}</dd>
      </dl>
      <div className="dupmeta">
        <span className="pill" style={{ color: look.color }} title={look.description}>
          <Icon name={look.icon} />
          {look.label}
        </span>
        {copy && <span className="pill">{copy}</span>}
      </div>
      <div className="row2">
        <button type="button" className="btn sm" onClick={onOpen}>
          <Icon name="eye" />
          Open
        </button>
        <button type="button" className="btn sm danger" aria-label={`Move ${p.fileName} to Trash`} onClick={onTrash} disabled={busy}>
          <Icon name="trash" />
          Trash
        </button>
      </div>
    </div>
  );
}

/** `/studio/duplicates` (media): groups of photos that are probably the same shot, side by side, to keep the best one. */
export function DuplicatesPage() {
  const groups = useDuplicates();
  const dismiss = useDismissDuplicates();
  const update = useUpdatePhotos();
  const toast = useToast();
  const undoToast = useUndoToast();
  const [open, setOpen] = useState<{ key: string; index: number } | null>(null);
  const fail = (e: Error) => toast({ icon: 'info', title: 'That didn’t work', sub: e.message });

  const trash = (p: DuplicatePhoto) =>
    update.mutate({ ids: [p.id], trash: true }, { onSuccess: (r) => undoToast({ icon: 'trash', title: `Moved ${p.fileName} to Trash` }, r.activityIds[0]), onError: fail });
  const notDuplicates = (g: DuplicateGroup) =>
    dismiss.mutate(
      g.photos.map((p) => p.id),
      { onSuccess: () => toast({ icon: 'check', title: 'Marked as not duplicates', sub: 'This group won’t be suggested again.' }), onError: fail },
    );

  const list = groups.data ?? [];
  return (
    <>
      <div className="toolbar">
        <h1>Possible duplicates</h1>
        <span className="meta">
          Photos from the same camera taken within 2 seconds, or that look alike. Keep the best one and move the rest to Trash.
        </span>
      </div>
      {groups.isError ? (
        <div className="empty">
          <h3>Couldn’t load possible duplicates</h3>
          {groups.error.message}
        </div>
      ) : !groups.data ? (
        <div className="sentinel">
          <div className="spinner" />
        </div>
      ) : !list.length ? (
        <div className="empty">
          <h3>No possible duplicates</h3>
          Nothing looks like a duplicate right now.
        </div>
      ) : (
        <>
          <p className="help" style={{ color: 'var(--muted)', margin: '0 0 12px' }}>
            {fmtCount(list.length)} {list.length === 1 ? 'group' : 'groups'}
          </p>
          {list.map((g) => (
            <section key={groupKey(g)} className="dupgroup" aria-label={`${g.photos.length} possible duplicates`}>
              <header>
                <b>{g.reasons.map((r) => REASON[r]).join(' · ')}</b>
                <span className="meta">{g.photos.length} photos</span>
                <button type="button" className="btn sm ghost" onClick={() => notDuplicates(g)} disabled={dismiss.isPending}>
                  <Icon name="x" />
                  Not duplicates
                </button>
              </header>
              <div className="duprow">
                {g.photos.map((p, i) => (
                  <DuplicateCard key={p.id} p={p} group={g.photos} busy={update.isPending} onOpen={() => setOpen({ key: groupKey(g), index: i })} onTrash={() => trash(p)} />
                ))}
              </div>
            </section>
          ))}
        </>
      )}
      {open && (
        // The group as currently listed; trashing one from the viewer changes the group, which closes the viewer.
        <StudioLightbox
          photos={list.find((g) => groupKey(g) === open.key)?.photos ?? []}
          index={open.index}
          onIndex={(index) => setOpen({ ...open, index })}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}
